import React, { useState, useEffect } from 'react';
import api from '../config/api';

function CreateItemTypeModal({ onClose, onCreate, itemType }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fields: []
  });

  const [currentField, setCurrentField] = useState({
    name: '',
    type: 'text',
    required: false,
    description: '',
    options: [],
    discover_in_image: true,
    in_view: true
  });

  const [currentOption, setCurrentOption] = useState('');
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationInfo, setMigrationInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (itemType) {
      // Ensure all fields have the in_view property (default to true)
      const fieldsWithInView = (itemType.fields || []).map(field => ({
        ...field,
        in_view: field.in_view !== undefined ? field.in_view : true
      }));
      
      setFormData({
        name: itemType.name || '',
        description: itemType.description || '',
        fields: fieldsWithInView
      });
    }
  }, [itemType]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFieldChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCurrentField({
      ...currentField,
      [e.target.name]: value
    });
  };

  const addField = () => {
    if (!currentField.name.trim()) return;
    
    setFormData({
      ...formData,
      fields: [...formData.fields, { ...currentField }]
    });
    
    setCurrentField({
      name: '',
      type: 'text',
      required: false,
      description: '',
      options: [],
      discover_in_image: true,
      in_view: true
    });
  };

  const removeField = (index) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter((_, i) => i !== index)
    });
    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
      setCurrentField({
        name: '',
        type: 'text',
        required: false,
        description: '',
        options: [],
        discover_in_image: true,
        in_view: true
      });
    }
  };

  const editField = (index) => {
    const fieldToEdit = formData.fields[index];
    setCurrentField({ ...fieldToEdit });
    setEditingFieldIndex(index);
  };

  const updateField = () => {
    if (!currentField.name.trim() || editingFieldIndex === null) return;
    
    const updatedFields = [...formData.fields];
    updatedFields[editingFieldIndex] = { ...currentField };
    
    setFormData({
      ...formData,
      fields: updatedFields
    });
    
    // Reset editing state
    setEditingFieldIndex(null);
    setCurrentField({
      name: '',
      type: 'text',
      required: false,
      description: '',
      options: [],
      discover_in_image: true,
      in_view: true
    });
  };

  const cancelEdit = () => {
    setEditingFieldIndex(null);
    setCurrentField({
      name: '',
      type: 'text',
      required: false,
      description: '',
      options: [],
      discover_in_image: true,
      in_view: true
    });
  };

  const addOption = () => {
    if (!currentOption.trim()) return;
    
    setCurrentField({
      ...currentField,
      options: [...currentField.options, currentOption.trim()]
    });
    
    setCurrentOption('');
  };

  const removeOption = (index) => {
    setCurrentField({
      ...currentField,
      options: currentField.options.filter((_, i) => i !== index)
    });
  };

  const handleOptionKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption();
    }
  };

  const moveField = (index, direction) => {
    const newFields = [...formData.fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newFields.length) {
      [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
      
      setFormData({
        ...formData,
        fields: newFields
      });
      
      // Update editing index if necessary
      if (editingFieldIndex === index) {
        setEditingFieldIndex(targetIndex);
      } else if (editingFieldIndex === targetIndex) {
        setEditingFieldIndex(index);
      }
    }
  };

  const requiresOptions = (fieldType) => {
    return ['radio', 'select', 'checkbox'].includes(fieldType);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    // For editing existing item types, check migration impact
    if (itemType) {
      setIsSubmitting(true);
      try {
        const response = await api.get(`/item-types/${itemType.id}/impact`);
        const impact = response.data;
        
        if (impact.affected_inventories > 0) {
          setMigrationInfo(impact);
          setShowMigrationDialog(true);
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Error checking migration impact:', error);
        // Continue with normal update if impact check fails
      }
      setIsSubmitting(false);
    }
    
    // No migration needed or new item type
    onCreate(formData);
  };

  const handleMigrationChoice = async (updateInventories) => {
    setShowMigrationDialog(false);
    setIsSubmitting(true);
    
    try {
      if (updateInventories) {
        // Use migration endpoint
        const response = await api.put(`/item-types/${itemType.id}/migrate`, {
          ...formData,
          update_inventories: true
        });
        
        // Show migration results
        if (response.data.migrated_inventories) {
          const result = response.data.migrated_inventories;
          alert(
            `✅ Migration completed!\n\n` +
            `• Updated ${result.migrated_count} of ${result.total_items} items\n` +
            `• ${result.errors.length} errors occurred\n\n` +
            `Item type has been updated with new field configuration.`
          );
        }
        
        // Call onCreate to trigger data refresh in parent
        onCreate(response.data.item_type);
        onClose();
      } else {
        // Use regular update endpoint (no migration)
        onCreate(formData);
      }
    } catch (error) {
      console.error('Error updating item type:', error);
      alert('Error updating item type: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal">
      <div className="modal-content">
        <h2>{itemType ? 'Edit Item Type' : 'Create New Item Type'}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Item Type Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g., Electronics, Books, Tools..."
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Describe what types of items this item type is for..."
            />
          </div>

          <div className="form-group">
            <label>Item Fields</label>
            
            <div style={{ border: '1px solid #ddd', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <h4>Add Field</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <input
                  type="text"
                  name="name"
                  placeholder="Field name (e.g., Serial Number, Brand)"
                  value={currentField.name}
                  onChange={handleFieldChange}
                />
                <select
                  name="type"
                  value={currentField.type}
                  onChange={handleFieldChange}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="textarea">Long Text</option>
                  <option value="select">Select (Single Choice)</option>
                  <option value="radio">Radio Buttons</option>
                  <option value="checkbox">Checkboxes (Multiple Choice)</option>
                </select>
              </div>
              <input
                type="text"
                name="description"
                placeholder="Field description (optional)"
                value={currentField.description}
                onChange={handleFieldChange}
                style={{ marginBottom: '1rem' }}
              />

              {/* Options configuration for select, radio, and checkbox fields */}
              {requiresOptions(currentField.type) && (
                <div style={{ marginBottom: '1rem' }}>
                  <h5>Configure Options</h5>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Option value (e.g., Small, Medium, Large)"
                      value={currentOption}
                      onChange={(e) => setCurrentOption(e.target.value)}
                      onKeyPress={handleOptionKeyPress}
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="btn" onClick={addOption} style={{ fontSize: '0.9rem' }}>
                      Add Option
                    </button>
                  </div>
                  
                  {currentField.options.length > 0 && (
                    <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                      {currentField.options.map((option, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          padding: '0.25rem',
                          borderBottom: index < currentField.options.length - 1 ? '1px solid #eee' : 'none'
                        }}>
                          <span style={{ fontSize: '0.9rem' }}>{option}</span>
                          <button 
                            type="button" 
                            className="btn btn-danger" 
                            onClick={() => removeOption(index)}
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p style={{ fontSize: '0.8rem', color: '#666', margin: '0.5rem 0' }}>
                    {currentField.type === 'checkbox' ? 
                      'Users can select multiple options' : 
                      'Users can select one option'
                    }
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    name="required"
                    checked={currentField.required}
                    onChange={handleFieldChange}
                    style={{ marginRight: '0.5rem', width: 'auto' }}
                  />
                  Required field
                </label>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    name="discover_in_image"
                    checked={currentField.discover_in_image}
                    onChange={handleFieldChange}
                    style={{ marginRight: '0.5rem', width: 'auto' }}
                  />
                  Discover in image
                </label>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    name="in_view"
                    checked={currentField.in_view}
                    onChange={handleFieldChange}
                    style={{ marginRight: '0.5rem', width: 'auto' }}
                  />
                  Show in view
                </label>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  type="button" 
                  className="btn" 
                  onClick={editingFieldIndex !== null ? updateField : addField}
                  disabled={requiresOptions(currentField.type) && currentField.options.length === 0}
                >
                  {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
                </button>
                {editingFieldIndex !== null && (
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={cancelEdit}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
              {requiresOptions(currentField.type) && currentField.options.length === 0 && (
                <p style={{ fontSize: '0.8rem', color: '#e74c3c', marginTop: '0.5rem' }}>
                  Please add at least one option for this field type
                </p>
              )}
            </div>

            {formData.fields.length > 0 && (
              <div>
                <h4>Defined Fields ({formData.fields.length})</h4>
                {formData.fields.map((field, index) => (
                  <div key={index} className="field-definition">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <strong>{field.name}</strong> ({field.type})
                        {field.required && <span style={{ color: '#e74c3c' }}> *</span>}
                        {field.discover_in_image && <span style={{ color: '#27ae60', fontSize: '0.8rem' }}> 📷</span>}
                        {field.description && <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>{field.description}</p>}
                        {field.options && field.options.length > 0 && (
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#555' }}>
                            Options: {field.options.slice(0, 3).join(', ')}
                            {field.options.length > 3 && ` +${field.options.length - 3} more`}
                          </p>
                        )}
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
                            <input
                              type="checkbox"
                              checked={field.in_view !== false}
                              onChange={(e) => {
                                const updatedFields = [...formData.fields];
                                updatedFields[index] = { ...field, in_view: e.target.checked };
                                setFormData({ ...formData, fields: updatedFields });
                              }}
                              style={{ marginRight: '0.3rem', width: 'auto' }}
                            />
                            Show in view
                          </label>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', minWidth: '28px' }}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => moveField(index, 'down')}
                            disabled={index === formData.fields.length - 1}
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', minWidth: '28px' }}
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                        <button 
                          type="button" 
                          className="btn btn-secondary" 
                          onClick={() => editField(index)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                        >
                          Edit
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-danger" 
                          onClick={() => removeField(index)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={!formData.name.trim() || isSubmitting}>
              {isSubmitting ? 'Processing...' : (itemType ? 'Update Item Type' : 'Create Item Type')}
            </button>
          </div>
        </form>
      </div>
      
      {showMigrationDialog && migrationInfo && (
        <div className="modal" style={{ zIndex: 10000 }}>
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <h2>Update Linked Inventories?</h2>
            <p>This item type is used by existing inventories:</p>
            
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '4px', 
              margin: '1rem 0' 
            }}>
              <strong>Impact Summary:</strong>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                <li>{migrationInfo.affected_inventories} inventories affected</li>
                <li>{migrationInfo.affected_items} items will be updated</li>
              </ul>
              
              {migrationInfo.sample_inventories.length > 0 && (
                <div>
                  <strong>Affected inventories include:</strong>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {migrationInfo.sample_inventories.map((name, index) => (
                      <li key={index}>{name}</li>
                    ))}
                    {migrationInfo.affected_inventories > migrationInfo.sample_inventories.length && (
                      <li><em>...and {migrationInfo.affected_inventories - migrationInfo.sample_inventories.length} more</em></li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            
            <div style={{ 
              backgroundColor: '#fff3cd', 
              padding: '1rem', 
              borderRadius: '4px', 
              border: '1px solid #ffeaa7',
              margin: '1rem 0' 
            }}>
              <strong>⚠️ Migration Notes:</strong>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.9rem' }}>
                <li>Field values that don't match the new schema will be cleared</li>
                <li>Removed fields will be permanently deleted from items</li>
                <li>Select/radio/checkbox values not in new options will be cleared</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
            
            <p><strong>Do you want to update all linked inventories with the new field configuration?</strong></p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
              <button 
                type="button" 
                className="btn" 
                onClick={() => handleMigrationChoice(false)}
                disabled={isSubmitting}
              >
                No, Update Type Only
              </button>
              <button 
                type="button" 
                className="btn" 
                onClick={() => handleMigrationChoice(true)}
                disabled={isSubmitting}
                style={{ backgroundColor: '#e74c3c', color: 'white' }}
              >
                {isSubmitting ? 'Migrating...' : 'Yes, Migrate All Items'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateItemTypeModal;